/**
 * Iter129 — NexusTrac Control panel.
 *
 * Consolidates Feature Permissions + Company Notifications + per-User
 * notification preferences into a single admin surface with tabs.
 * Replaces the standalone /admin/permissions and
 * /admin/notification-permissions menu tiles.
 */
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ShieldCheck, BellRing, UserCog2 } from 'lucide-react';
import AdminMainHubPermissions from './AdminMainHubPermissions';
import MainHubNotificationPermissions from './MainHubNotificationPermissions';
import NotificationPreferencesTab from '../components/NotificationPreferencesTab';

export default function NexusTracControl() {
  return (
    <div className="space-y-6" data-testid="nexustrac-control-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-cyan-300" />
        </div>
        <div>
          <div className="text-xs text-cyan-300 uppercase tracking-wider font-mono">Admin · NexusTrac</div>
          <h1 className="text-2xl font-bold text-white">NexusTrac Control</h1>
          <p className="text-slate-400 text-sm">
            Every feature toggle, notification event, and personal alert setting for the company —
            in one place.
          </p>
        </div>
      </div>

      <Tabs defaultValue="features" className="w-full">
        <TabsList className="bg-[#131A2A] border border-[#334155]">
          <TabsTrigger value="features" data-testid="tab-features">
            <ShieldCheck className="w-4 h-4 mr-1" /> Feature Permissions
          </TabsTrigger>
          <TabsTrigger value="company-notifs" data-testid="tab-company-notifs">
            <BellRing className="w-4 h-4 mr-1" /> Company Notifications
          </TabsTrigger>
          <TabsTrigger value="my-notifs" data-testid="tab-my-notifs">
            <UserCog2 className="w-4 h-4 mr-1" /> My Notifications
          </TabsTrigger>
        </TabsList>
        <TabsContent value="features" className="mt-4">
          <AdminMainHubPermissions />
        </TabsContent>
        <TabsContent value="company-notifs" className="mt-4">
          <MainHubNotificationPermissions />
        </TabsContent>
        <TabsContent value="my-notifs" className="mt-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-white text-lg">My Personal Notifications</CardTitle>
              <p className="text-xs text-slate-400">
                Override the company defaults for your own account. Critical
                security events cannot be silenced.
              </p>
            </CardHeader>
            <CardContent>
              <NotificationPreferencesTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
